

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FiExternalLink, FiSmile } from 'react-icons/fi';
import CustomEmojiPicker from './Common/EmojiPicker';
import ModalCloseButton from './Common/ModalCloseButton';
import NewsFeed from './NewsFeed';
import styled from 'styled-components';
import { getCustomEmojiSizeForArea, subscribeEmojiSettings } from '../utils/emojiSettings';
import { usePersistedNewsGoogleFonts } from '../hooks/usePersistedNewsGoogleFonts';
import {
  GOOGLE_FONTS_WELCOME_CATALOG,
  googleFontFamilyCssValue,
  collectGoogleFontNamesFromStacks,
  syncNewsGoogleFontLinkElements,
  removeAllNewsGoogleFontPreviewLinks,
  syncNewsCustomCssLinks,
  removeAllNewsCustomCssPreviewLinks,
} from '../utils/googleFontsWelcome';
import {
  NEWS_CONTENT_BACKGROUND_PRESETS,
  NEWS_CARD_BACKGROUND_PRESETS,
  NEWS_POLL_CARD_BACKGROUND_PRESETS,
  getNewsContentBackgroundCss,
  getNewsCardBackgroundCss,
} from '../utils/newsContentBackgrounds';
import { extractFirstCssTextColorFromHtml } from '../utils/newsContentColorMeta';
import { isNewsHtmlBodyEmpty } from '../utils/newsHtmlBodyEmpty';

const ModalWrapper = styled.div`
  position: fixed !important;
  top: 0 !important;
  left: 320px !important;
  right: 0 !important;
  bottom: 0 !important;
  width: calc(100vw - 320px) !important;
  height: 100vh !important;
  margin: 0 !important;
  padding: 0 !important;
  z-index: 1000;
  display: flex !important;
  align-items: stretch !important;
  justify-content: flex-start !important;
  background: rgba(0,0,0,0.45);
  pointer-events: auto;
  
  @media (max-width: 768px) {
    left: 0 !important;
    right: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    padding: 12px;
    align-items: flex-start;
    justify-content: center;
  }
`;

const ModalContent = styled.div`
  background: var(--modal-news-background, var(--modal-default-background, linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)));
  background-image: var(--modal-news-background-image, var(--modal-default-background-image, none));
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 0;
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
  min-width: 100% !important;
  min-height: 100% !important;
  box-sizing: border-box;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  position: relative;
  color: #232931;
  padding: 28px 36px 24px;
  overflow-y: auto;
  overflow-x: hidden;
  border: none;
  backdrop-filter: blur(3px);
  
  @media (max-width: 768px) {
    border-radius: 20px;
    box-shadow: 0 12px 48px 0 rgba(67,233,123,0.12), 0 2px 10px 0 rgba(56, 249, 215, 0.06);
    border: 1.5px solid ${({ theme }) => theme?.colors?.panelBorder || 'rgba(67,233,123,0.16)'};
    min-width: 0;
    max-width: 100%;
    padding: 18px 16px 14px;
    height: calc(100vh - 24px);
  }
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  padding: 14px 6px 16px;
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.divider || 'rgba(67,233,123,0.12)'};
  margin-bottom: 16px;
  @media (max-width: 980px) {
    gap: 10px;
    padding-top: 10px;
    padding-bottom: 10px;
    margin-bottom: 12px;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  h2 {
    margin: 0;
    font-size: 1.28rem;
    font-weight: 800;
    letter-spacing: 0.01em;
    color: ${({ theme }) => theme?.colors?.headerTitle || '#ffffff'};
  }
  @media (max-width: 980px) {
    h2 { font-size: 1.1rem; }
  }
`;

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-start;
  width: 100%;
  @media (max-width: 980px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  font-size: 0.93em;
  font-weight: 700;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  color: #fff;
  background: ${({ variant, theme }) => variant === 'news'
    ? (theme?.colors?.primaryGradient || 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)')
    : (theme?.colors?.secondaryGradient || 'linear-gradient(135deg, #ff512f 0%, #dd2476 100%)')};
  box-shadow: 0 2px 8px #43e97b33;
  transition: transform .12s ease, box-shadow .2s ease, opacity .2s ease;
  will-change: transform;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px #43e97b44; }
  &:active { transform: translateY(0); box-shadow: 0 2px 8px #43e97b33; }
  @media (max-width: 980px) {
    padding: 8px 14px;
    font-size: 0.9em;
  }
`;

const TabButton = styled.button`
  padding: 6px 18px;
  font-size: 0.93em;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${({ active }) => active
    ? 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)'
    : 'rgba(40, 46, 54, 0.9)'};
  color: ${({ active }) => active ? '#fff' : '#cfd8dc'};
  box-shadow: ${({ active }) => active ? '0 2px 10px rgba(0,176,155,0.28)' : 'inset 0 0 0 1px rgba(255,255,255,0.06)'};
  transition: background .2s,color .2s, box-shadow .2s, transform .12s ease;
  letter-spacing: 0.01em;
  &:hover { transform: translateY(-1px); }
  @media (max-width: 980px) {
    padding: 6px 14px;
    font-size: 0.9em;
  }
`;

const IconTabButton = styled(TabButton)`
  padding: 8px;
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 980px) {
    width: 38px;
    height: 38px;
    padding: 6px;
  }
`;

const TooltipWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover > span[data-tooltip] {
    opacity: 1;
    transform: translate(-50%, -6px);
    pointer-events: auto;
  }
`;

const Tooltip = styled.span`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translate(-50%, 0);
  background: rgba(20, 24, 28, 0.96);
  color: #e6f7ef;
  font-size: 12px;
  line-height: 1;
  padding: 8px 10px;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.28);
  white-space: nowrap;
  opacity: 0;
  transition: opacity .16s ease, transform .16s ease;
  pointer-events: none;
  z-index: 5;
  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 6px 6px 0 6px;
    border-style: solid;
    border-color: rgba(20, 24, 28, 0.96) transparent transparent transparent;
  }
`;

const GiftIcon = ({ size = 20, colored = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="giftGrad" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#ff5f6d"/>
        <stop offset="100%" stopColor="#ffc371"/>
      </linearGradient>
    </defs>
    <path d="M20 7h-2.18A3 3 0 0 0 15 3a2.5 2.5 0 0 0-2.5 2.5V7H12V5.5A2.5 2.5 0 0 0 9.5 3 3 3 0 0 0 6.18 7H4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h1v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8h1a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1ZM9.5 5a1 1 0 0 1 1 1V7H8.28A1.28 1.28 0 0 1 7 5.72 1.72 1.72 0 0 1 8.72 4 1.78 1.78 0 0 1 9.5 5Zm3 0a1 1 0 0 1 1-1 1.78 1.78 0 0 1 .78 1 1.72 1.72 0 0 1 1.72 1.72A1.28 1.28 0 0 1 13.72 7H12.5V6a1 1 0 0 1 0-1ZM5 9h6v2H5V9Zm2 4h4v7H7v-7Zm10 7h-4v-7h4v7Zm2-9h-6V9h6v2Z" fill={colored ? 'url(#giftGrad)' : 'currentColor'}/>
  </svg>
);

const ContentScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  @media (max-width: 980px) {
    padding-right: 4px;
  }
`;

// Overlay and small modal card used for add news / poll
const FullscreenOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme?.colors?.overlay || 'rgba(20, 24, 28, 0.82)'};
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SmallCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  min-width: 420px;
  max-width: 560px;
  width: 100%;
  padding: 34px 34px 26px;
  box-shadow: 0 10px 44px #2193b044, 0 0 20px #43e97b55;
  position: relative;
  color: #232931;
  font-size: 1.08em;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  @media (max-width: 520px) {
    min-width: 0;
    margin: 0 12px;
    border-radius: 18px;
    padding: 22px 18px 16px;
  }
`;

// Comment-modal-like styles for Add News (match NewsFeed comment modal aesthetics)
const AddModalBg = styled.div`
  position: fixed;
  top: 0; left: max(calc(380px + max((100vw - 380px - 1200px)/2, 0px) - 585px), 0px); right: 0; bottom: 0;
  z-index: 2000;
  background: rgba(34,40,49,0.82);
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  overflow: hidden;
  height: 100vh;
  min-height: 0;
`;

const AddModal = styled.div`
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  min-height: 0;
  background: #fff;
  border-radius: 0;
  box-shadow: 0 8px 40px #2193b033, 0 0 0 2px #43e97b44;
  padding: 32px 28px 24px 28px;
  position: relative;
  font-family: inherit;
  color: #222;
  font-size: 1.08em;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

/** Прокрутка длинной формы «Добавить новость» на десктопе (flex + min-height:0). */
const AddModalFormScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-right: 6px;
  margin-right: -2px;
`;

const SmallCloseX = styled.button`
  position: absolute;
  top: 16px;
  right: 20px;
  font-size: 26px;
  background: none;
  border: none;
  cursor: pointer;
  color: #2193b0;
`;

const SmallTitle = styled.h3`
  margin: 0 0 16px;
  color: ${({ theme }) => theme?.colors?.primary || '#2193b0'};
  font-weight: 800;
  font-size: 1.2em;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const TextInput = styled.input`
  font-weight: 600;
  font-size: 1em;
  min-width: 220px;
  border-radius: 10px;
  border: 1.5px solid ${({ theme }) => theme?.colors?.primary || '#2193b0'};
  padding: 10px 14px;
  width: 100%;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  font-size: 1.02em;
  background: #f9fafb;
  border-radius: 10px;
  border: 1.5px solid ${({ theme }) => theme?.colors?.primary || '#2193b0'};
  padding: 10px 14px;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #2193b0 0%, #43e97b 100%);
    color: #fff;
  border: none;
  border-radius: 14px;
  font-weight: 700;
  font-size: 1.05em;
  padding: 12px 28px;
  cursor: pointer;
  box-shadow: 0 2px 12px #43e97b33;
  margin-top: 4px;
  transition: background .2s,color .2s, box-shadow .2s, opacity .2s;
  @media (max-width: 520px) {
    font-size: 1em;
    padding: 10px 22px;
  }
`;


const NewsModal = ({ open, onClose, initialOpenAddModal = false, initialOpenPollModal = false, onConsumeInitialOpen }) => {
  const [tab, setTab] = useState('news');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [isPollSending, setIsPollSending] = useState(false);
  const [pollCardBackgroundId, setPollCardBackgroundId] = useState('');
  const [pollCardImageUrlDesktop, setPollCardImageUrlDesktop] = useState('');
  const [pollCardImageUrlMobile, setPollCardImageUrlMobile] = useState('');
  const [pollCardImageUploadingDesktop, setPollCardImageUploadingDesktop] = useState(false);
  const [pollCardImageUploadingMobile, setPollCardImageUploadingMobile] = useState(false);
  const pollModalCardImageInputRefDesktop = useRef(null);
  const pollModalCardImageInputRefMobile = useRef(null);

  const resetPollModalFields = useCallback(() => {
    setPollQuestion('');
    setPollOptions(['', '']);
    setPollCardBackgroundId('');
    setPollCardImageUrlDesktop('');
    setPollCardImageUrlMobile('');
    if (pollModalCardImageInputRefDesktop.current) pollModalCardImageInputRefDesktop.current.value = '';
    if (pollModalCardImageInputRefMobile.current) pollModalCardImageInputRefMobile.current.value = '';
  }, []);

  const handlePollModalCardImageFile = useCallback(async (e, variant) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !String(file.type || '').startsWith('image/')) {
      alert('Выберите файл изображения');
      return;
    }
    const authToken = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!authToken) {
      alert('Нужна авторизация');
      return;
    }
    const setBusy =
      variant === 'mobile' ? setPollCardImageUploadingMobile : setPollCardImageUploadingDesktop;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch('/api/news/card-image', {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
      if (data.url) {
        if (variant === 'mobile') setPollCardImageUrlMobile(data.url);
        else setPollCardImageUrlDesktop(data.url);
      }
    } catch (err) {
      alert(err.message || 'Не удалось загрузить изображение');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (open && (initialOpenAddModal || initialOpenPollModal)) {
      if (initialOpenAddModal) setShowAddModal(true);
      if (initialOpenPollModal) setShowPollModal(true);
      onConsumeInitialOpen?.();
    }
  }, [open, initialOpenAddModal, initialOpenPollModal, onConsumeInitialOpen]);

  if (!open) return null;
  const token = localStorage.getItem('token');
  const userRaw = localStorage.getItem('user');
  let userRole = '';
  try {
    userRole = JSON.parse(userRaw)?.role || '';
  } catch {}
  const normalizedRole = String(userRole || '').trim().toLowerCase();
  const canManageNews = ['admin', 'hr', 'hr manager', 'hr_manager', 'human resources', 'human_resources'].includes(normalizedRole);

  // Функция для перехода обратно к новостям
  const handleBackToNews = () => setTab('news');

  return (
    <ModalWrapper onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalCloseButton onClick={onClose} aria-label="Закрыть" />
        <HeaderBar>
          {canManageNews && (
            <ActionsGroup>
              <TooltipWrapper>
                <ActionButton variant="news" onClick={() => setShowAddModal(true)}>Добавить новость</ActionButton>
                <Tooltip data-tooltip>Добавить новость</Tooltip>
              </TooltipWrapper>
              <TooltipWrapper>
                <ActionButton variant="poll" onClick={() => setShowPollModal(true)}>Голосование</ActionButton>
                <Tooltip data-tooltip>Голосование</Tooltip>
              </TooltipWrapper>
              <TooltipWrapper>
                <TabButton active={tab==='news'} onClick={()=>setTab('news')}>Новости</TabButton>
                <Tooltip data-tooltip>Новости</Tooltip>
              </TooltipWrapper>
              
            </ActionsGroup>
          )}
        </HeaderBar>
        {/* Tabs moved into header actions row */}
        <ContentScroll>
          <NewsFeed modal onClose={onClose} token={token} />
        </ContentScroll>
        {/* Модалка добавления новости */}
        {showAddModal && (
          <AddModalBg onClick={() => setShowAddModal(false)}>
            <AddModal onClick={e => e.stopPropagation()}>
              <div style={{ flexShrink: 0, position: 'relative', paddingRight: 40, marginBottom: 10 }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    fontSize: 24,
                    lineHeight: 1,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#2193b0',
                    padding: 4,
                  }}
                >
                  ×
                </button>
                <h3 style={{ margin: 0, color: '#e74c3c', fontSize: '1.2em' }}>Добавить новость</h3>
            </div>
              <AddModalFormScroll onClick={e => e.stopPropagation()}>
                <AddNewsForm token={token} onSuccess={() => setShowAddModal(false)} />
              </AddModalFormScroll>
            </AddModal>
          </AddModalBg>
        )}
        {/* Модалка создания голосования */}
        {showPollModal && (
          <FullscreenOverlay
            onClick={() => {
              setShowPollModal(false);
              resetPollModalFields();
            }}
          >
            <SmallCard onClick={e => e.stopPropagation()}>
              <SmallCloseX
                onClick={() => {
                  setShowPollModal(false);
                  resetPollModalFields();
                }}
              >
                ×
              </SmallCloseX>
              <SmallTitle>Создать голосование</SmallTitle>
              <Form onSubmit={async e => {
                e.preventDefault();
                if (!pollQuestion.trim() || pollOptions.some(opt => !opt.trim())) return;
                setIsPollSending(true);
                try {
                  const res = await fetch('/api/news/poll', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({
                      question: pollQuestion.trim(),
                      options: pollOptions.map(opt => opt.trim()),
                      cardBackground: pollCardBackgroundId.trim() || undefined,
                      cardImageUrl: pollCardImageUrlDesktop.trim() || undefined,
                      cardImageUrlMobile: pollCardImageUrlMobile.trim() || undefined,
                    }),
                  });
                  if (res.ok) {
                  setShowPollModal(false);
                    resetPollModalFields();
                    window.dispatchEvent(new CustomEvent('news-published'));
                  } else {
                    const errBody = await res.json().catch(() => ({}));
                    alert(errBody?.error || 'Не удалось создать голосование');
                  }
                } finally {
                  setIsPollSending(false);
                }
              }}>
                <TextInput
                  name="question"
                  placeholder="Тема голосования"
                  value={pollQuestion}
                  onChange={e => setPollQuestion(e.target.value)}
                  required
                  maxLength={120}
                  autoFocus
                />
                {pollOptions.map((opt, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TextInput
                      type="text"
                      value={opt}
                      onChange={e => setPollOptions(
                        pollOptions.map((o, i) => i === idx ? e.target.value : o)
                      )}
                      placeholder={`Вариант ${idx + 1}`}
                      required
                      maxLength={60}
                    />
                    {pollOptions.length > 2 && (
                      <button type="button" onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: 22, cursor: 'pointer', marginLeft: 4 }} title="Удалить вариант">×</button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setPollOptions([...pollOptions, ''])} style={{ background: 'none', border: '1.5px dashed #2193b0', color: '#2193b0', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', marginTop: 2 }}>+ Добавить вариант</button>
                <div
                  style={{
                    padding: 10,
                    borderRadius: 10,
                    border: '1px solid #cbd5e1',
                    background: '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    marginTop: 4,
                  }}
                >
                  <div style={{ fontWeight: 700, color: '#334155', fontSize: '0.88em' }}>Фон карточки «Голосование»</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      title="Образец градиента"
                      style={{
                        width: 32,
                        height: 22,
                        borderRadius: 6,
                        border: '1px solid #94a3b8',
                        flexShrink: 0,
                        background: pollCardBackgroundId
                          ? getNewsCardBackgroundCss(pollCardBackgroundId)
                          : 'repeating-conic-gradient(#e2e8f0 0% 25%, #cbd5e1 0% 50%) 50% / 10px 10px',
                      }}
                    />
                    <select
                      value={pollCardBackgroundId}
                      onChange={(e) => setPollCardBackgroundId(e.target.value)}
                      aria-label="Градиент фона карточки опроса"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        maxWidth: '100%',
                        padding: '6px 8px',
                        borderRadius: 8,
                        border: '1px solid #cbd5e1',
                        fontSize: '0.85em',
                      }}
                    >
                      <option value="">По умолчанию</option>
                      <optgroup label="Светлые">
                        {NEWS_POLL_CARD_BACKGROUND_PRESETS.filter((p) => p.group === 'light').map((p) => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Тёмные">
                        {NEWS_POLL_CARD_BACKGROUND_PRESETS.filter((p) => p.group === 'dark').map((p) => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                  <div style={{ fontSize: '0.72em', color: '#64748b', lineHeight: 1.4 }}>
                    Фото фона: отдельно для телефона и ПК. JPEG, PNG, GIF, WebP, до 8 МБ.
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#475569', fontSize: '0.78em', marginBottom: 4 }}>Для телефона</div>
                    <input
                      ref={pollModalCardImageInputRefMobile}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      disabled={pollCardImageUploadingMobile}
                      onChange={(ev) => handlePollModalCardImageFile(ev, 'mobile')}
                      style={{ width: '100%', fontSize: '0.8em' }}
                    />
                    {pollCardImageUploadingMobile ? (
                      <div style={{ marginTop: 4, fontSize: '0.72em', color: '#64748b' }}>Загрузка…</div>
                    ) : null}
                    {pollCardImageUrlMobile ? (
                      <div style={{ marginTop: 6 }}>
                        <img
                          src={pollCardImageUrlMobile}
                          alt=""
                          style={{
                            maxHeight: 64,
                            width: '100%',
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e2e8f0',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setPollCardImageUrlMobile('');
                            if (pollModalCardImageInputRefMobile.current) {
                              pollModalCardImageInputRefMobile.current.value = '';
                            }
                          }}
                          style={{
                            marginTop: 4,
                            border: 'none',
                            background: 'transparent',
                            color: '#dc2626',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: '0.78em',
                          }}
                        >
                          Убрать (телефон)
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#475569', fontSize: '0.78em', marginBottom: 4 }}>Для ПК</div>
                    <input
                      ref={pollModalCardImageInputRefDesktop}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      disabled={pollCardImageUploadingDesktop}
                      onChange={(ev) => handlePollModalCardImageFile(ev, 'desktop')}
                      style={{ width: '100%', fontSize: '0.8em' }}
                    />
                    {pollCardImageUploadingDesktop ? (
                      <div style={{ marginTop: 4, fontSize: '0.72em', color: '#64748b' }}>Загрузка…</div>
                    ) : null}
                    {pollCardImageUrlDesktop ? (
                      <div style={{ marginTop: 6 }}>
                        <img
                          src={pollCardImageUrlDesktop}
                          alt=""
                          style={{
                            maxHeight: 64,
                            width: '100%',
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e2e8f0',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setPollCardImageUrlDesktop('');
                            if (pollModalCardImageInputRefDesktop.current) {
                              pollModalCardImageInputRefDesktop.current.value = '';
                            }
                          }}
                          style={{
                            marginTop: 4,
                            border: 'none',
                            background: 'transparent',
                            color: '#dc2626',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: '0.78em',
                          }}
                        >
                          Убрать (ПК)
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
                <SubmitButton type="submit" style={{ opacity: isPollSending ? 0.7 : 1 }} disabled={isPollSending}>
                  {isPollSending ? 'Создание...' : 'Создать'}
                </SubmitButton>
              </Form>
            </SmallCard>
          </FullscreenOverlay>
        )}
      </ModalContent>
    </ModalWrapper>
  );
};

const newsDesktopFontSelectStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1.5px solid #cbd5e1',
  background: '#fff',
  color: '#111',
  fontSize: '1em',
  marginBottom: 8,
  boxSizing: 'border-box',
};

// Форма добавления новости
function AddNewsForm({ token, onSuccess }) {
  const [form, setForm] = useState({ title: '', content: '' });
  const [showContentModal, setShowContentModal] = useState(false);
  const [tempContent, setTempContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const editorRef = useRef(null);
  const editorSavedRangeRef = useRef(null);
  const [textColor, setTextColor] = useState('#2193b0');
  const [bgColor, setBgColor] = useState('#fff59d');
  const [contentBackgroundId, setContentBackgroundId] = useState('');
  const [cardBackgroundId, setCardBackgroundId] = useState('');
  const [newsCardImageUrlDesktop, setNewsCardImageUrlDesktop] = useState('');
  const [newsCardImageUrlMobile, setNewsCardImageUrlMobile] = useState('');
  const [newsCardImageUploadingDesktop, setNewsCardImageUploadingDesktop] = useState(false);
  const [newsCardImageUploadingMobile, setNewsCardImageUploadingMobile] = useState(false);
  const newsCardImageInputRefDesktop = useRef(null);
  const newsCardImageInputRefMobile = useRef(null);
  const [newsCardVideoUrlDesktop, setNewsCardVideoUrlDesktop] = useState('');
  const [newsCardVideoUrlMobile, setNewsCardVideoUrlMobile] = useState('');
  const [newsCardVideoUploadingDesktop, setNewsCardVideoUploadingDesktop] = useState(false);
  const [newsCardVideoUploadingMobile, setNewsCardVideoUploadingMobile] = useState(false);
  const newsCardVideoInputRefDesktop = useRef(null);
  const newsCardVideoInputRefMobile = useRef(null);
  const [titleColor, setTitleColor] = useState('');
  const [contentColor, setContentColor] = useState('');
  const [titleFontSize, setTitleFontSize] = useState('');
  const [contentFontSize, setContentFontSize] = useState('');
  const [titleFontWeight, setTitleFontWeight] = useState('');
  const [contentFontWeight, setContentFontWeight] = useState('');
  const [newsCustomFontUrl, setNewsCustomFontUrl] = useState('');
  const [newsCustomFontName, setNewsCustomFontName] = useState('');
  const [newsCustomFontDataUrl, setNewsCustomFontDataUrl] = useState('');
  const [customEmojiMap, setCustomEmojiMap] = useState({});
  const [emojiSizes, setEmojiSizes] = useState({
    news: getCustomEmojiSizeForArea('news')
  });

  const {
    families: newsGoogleFontFamilies,
    load: loadNewsEditorGoogleFonts,
    addFamily: persistNewsGoogleFontFamily,
    removeFamily: unpersistNewsGoogleFontFamily,
  } = usePersistedNewsGoogleFonts();
  const [titleFontFamily, setTitleFontFamily] = useState('Arial, sans-serif');
  const [contentFontFamily, setContentFontFamily] = useState('Arial, sans-serif');
  const [googleFontsPickerOpen, setGoogleFontsPickerOpen] = useState(false);
  const [googleFontsSearch, setGoogleFontsSearch] = useState('');
  const [manualGoogleFontName, setManualGoogleFontName] = useState('');
  const googleFontsPickerApplyToRef = useRef('title');

  useEffect(() => {
    void loadNewsEditorGoogleFonts();
  }, [loadNewsEditorGoogleFonts]);

  useEffect(() => {
    syncNewsGoogleFontLinkElements(newsGoogleFontFamilies, { preview: true });
    return () => removeAllNewsGoogleFontPreviewLinks();
  }, [newsGoogleFontFamilies]);

  useEffect(() => {
    const u = newsCustomFontUrl.trim();
    syncNewsCustomCssLinks(u ? [u] : [], { preview: true });
    return () => removeAllNewsCustomCssPreviewLinks();
  }, [newsCustomFontUrl]);

  useEffect(() => {
    document.getElementById('news-modal-add-local-font-style')?.remove();
    if (!newsCustomFontDataUrl || !newsCustomFontName) return;
    const style = document.createElement('style');
    style.id = 'news-modal-add-local-font-style';
    const n = String(newsCustomFontName).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    style.textContent = `@font-face{font-family:'${n}';src:url(${JSON.stringify(newsCustomFontDataUrl)});font-display:swap;}`;
    document.head.appendChild(style);
    return () => document.getElementById('news-modal-add-local-font-style')?.remove();
  }, [newsCustomFontDataUrl, newsCustomFontName]);

  const addNewsGoogleFont = useCallback(
    (rawName) => {
      const t = String(rawName || '').trim();
      if (!t) return;
      const target = googleFontsPickerApplyToRef.current;
      const css = googleFontFamilyCssValue(t);
      if (target === 'content') setContentFontFamily(css);
      else setTitleFontFamily(css);
      setGoogleFontsPickerOpen(false);
      setManualGoogleFontName('');
      setGoogleFontsSearch('');
      void persistNewsGoogleFontFamily(t);
    },
    [persistNewsGoogleFontFamily]
  );

  const removeNewsGoogleFont = useCallback(
    (rawName) => {
      const t = String(rawName || '').trim();
      if (!t) return;
      const removedCss = googleFontFamilyCssValue(t);
      setTitleFontFamily((f) => (f === removedCss ? 'Arial, sans-serif' : f));
      setContentFontFamily((f) => (f === removedCss ? 'Arial, sans-serif' : f));
      void unpersistNewsGoogleFontFamily(t);
    },
    [unpersistNewsGoogleFontFamily]
  );

  const captureEditorRange = useCallback(() => {
    const el = editorRef.current;
    if (!el || typeof window === 'undefined') return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    try {
      const r = sel.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) {
        editorSavedRangeRef.current = r.cloneRange();
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  const handleNewsFontFileUpload = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      e.target.value = '';
      return;
    }
    const validExtensions = ['.ttf', '.otf', '.woff', '.woff2'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExtension)) {
      alert('Неверный формат файла. Пожалуйста, выберите файл .ttf, .otf, .woff или .woff2');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => {
      alert('Ошибка при чтении файла шрифта.');
      e.target.value = '';
    };
    reader.onload = (event) => {
      try {
        const dataUrl = event.target.result;
        let fontName = file.name.replace(/\.[^.]+$/, '');
        fontName = fontName.replace(/[^a-zA-Z0-9\s\-_]/g, '').trim() || 'CustomFont';
        setNewsCustomFontUrl('');
        setNewsCustomFontName(fontName);
        setNewsCustomFontDataUrl(dataUrl);
      } catch {
        alert('Ошибка при обработке файла шрифта.');
      }
      e.target.value = '';
    };
    reader.readAsDataURL(file);
  }, []);

  const handleNewsCardImageFile = useCallback(
    async (e, variant) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !String(file.type || '').startsWith('image/')) {
        alert('Выберите файл изображения');
        return;
      }
      if (!token) {
        alert('Нужна авторизация');
        return;
      }
      const setBusy =
        variant === 'mobile' ? setNewsCardImageUploadingMobile : setNewsCardImageUploadingDesktop;
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append('image', file);
        const res = await fetch('/api/news/card-image', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
        if (data.url) {
          if (variant === 'mobile') {
            setNewsCardImageUrlMobile(data.url);
            setNewsCardVideoUrlMobile('');
          } else {
            setNewsCardImageUrlDesktop(data.url);
            setNewsCardVideoUrlDesktop('');
          }
        }
      } catch (err) {
        alert(err.message || 'Не удалось загрузить изображение');
      } finally {
        setBusy(false);
      }
    },
    [token]
  );

  const handleNewsCardVideoFile = useCallback(
    async (e, variant) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      const okMp4 =
        String(file.type || '').toLowerCase().includes('mp4') ||
        /\.mp4$/i.test(file.name || '');
      if (!file || !okMp4) {
        alert('Выберите видео в формате MP4');
        return;
      }
      if (!token) {
        alert('Нужна авторизация');
        return;
      }
      const setBusy =
        variant === 'mobile' ? setNewsCardVideoUploadingMobile : setNewsCardVideoUploadingDesktop;
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append('video', file);
        const res = await fetch('/api/news/card-video', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
        if (data.url) {
          if (variant === 'mobile') {
            setNewsCardVideoUrlMobile(data.url);
            setNewsCardImageUrlMobile('');
          } else {
            setNewsCardVideoUrlDesktop(data.url);
            setNewsCardImageUrlDesktop('');
          }
        }
      } catch (err) {
        alert(err.message || 'Не удалось загрузить видео');
      } finally {
        setBusy(false);
      }
    },
    [token]
  );

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = e => {
    e.preventDefault();
    const hasCardVideo = Boolean(newsCardVideoUrlDesktop.trim() || newsCardVideoUrlMobile.trim());
    const titleTrim = (form.title || '').trim();
    const bodyEmpty = isNewsHtmlBodyEmpty(form.content);
    if (!hasCardVideo && (!titleTrim || bodyEmpty)) {
      alert('Укажите заголовок и текст новости или загрузите видео для карточки (MP4).');
      return;
    }
    setIsSending(true);
    const fromStacks = collectGoogleFontNamesFromStacks(titleFontFamily, contentFontFamily);
    const localNameTrim =
      newsCustomFontName && newsCustomFontDataUrl ? String(newsCustomFontName).trim() : '';
    const mergedFromStacks = fromStacks.filter((n) => n && n !== localNameTrim);
    const mergedGoogleFamilies = [...new Set([...newsGoogleFontFamilies, ...mergedFromStacks])];
    const payload = {
      title: titleTrim,
      content: bodyEmpty ? '' : form.content,
      titleFontFamily: titleFontFamily && titleFontFamily !== 'Arial, sans-serif' ? titleFontFamily : undefined,
      contentFontFamily: contentFontFamily && contentFontFamily !== 'Arial, sans-serif' ? contentFontFamily : undefined,
      newsGoogleFontFamilies: mergedGoogleFamilies.length > 0 ? mergedGoogleFamilies : undefined,
      newsCustomFontCssUrl: newsCustomFontUrl.trim() || undefined,
      newsLocalFontsJson:
        newsCustomFontName && newsCustomFontDataUrl
          ? JSON.stringify({
              shared: { name: newsCustomFontName, dataUrl: newsCustomFontDataUrl },
            })
          : undefined,
      titleColor: titleColor.trim() || undefined,
      contentColor:
        contentColor.trim() ||
        extractFirstCssTextColorFromHtml(form.content) ||
        undefined,
      titleFontSize: titleFontSize.trim() || undefined,
      contentFontSize: contentFontSize.trim() || undefined,
      titleFontWeight: titleFontWeight.trim() || undefined,
      contentFontWeight: contentFontWeight.trim() || undefined,
      contentBackground: contentBackgroundId.trim() || undefined,
      cardBackground: cardBackgroundId.trim() || undefined,
      cardImageUrl: newsCardImageUrlDesktop.trim() || undefined,
      cardImageUrlMobile: newsCardImageUrlMobile.trim() || undefined,
      cardVideoUrl: newsCardVideoUrlDesktop.trim() || undefined,
      cardVideoUrlMobile: newsCardVideoUrlMobile.trim() || undefined,
    };
    fetch('/api/news', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('news post failed');
        setForm({ title: '', content: '' });
        if (editorRef.current) editorRef.current.innerHTML = '';
        setTitleFontFamily('Arial, sans-serif');
        setContentFontFamily('Arial, sans-serif');
        setContentBackgroundId('');
        setCardBackgroundId('');
        setNewsCardImageUrlDesktop('');
        setNewsCardImageUrlMobile('');
        setNewsCardImageUploadingDesktop(false);
        setNewsCardImageUploadingMobile(false);
        if (newsCardImageInputRefDesktop.current) newsCardImageInputRefDesktop.current.value = '';
        if (newsCardImageInputRefMobile.current) newsCardImageInputRefMobile.current.value = '';
        setNewsCardVideoUrlDesktop('');
        setNewsCardVideoUrlMobile('');
        setNewsCardVideoUploadingDesktop(false);
        setNewsCardVideoUploadingMobile(false);
        if (newsCardVideoInputRefDesktop.current) newsCardVideoInputRefDesktop.current.value = '';
        if (newsCardVideoInputRefMobile.current) newsCardVideoInputRefMobile.current.value = '';
        setTitleColor('');
        setContentColor('');
        setTitleFontSize('');
        setContentFontSize('');
        setTitleFontWeight('');
        setContentFontWeight('');
        setNewsCustomFontUrl('');
        setNewsCustomFontName('');
        setNewsCustomFontDataUrl('');
        editorSavedRangeRef.current = null;
        setIsSending(false);
        if (onSuccess) onSuccess();
      })
      .catch(() => setIsSending(false));
  };
  // Load custom emojis list for inline rendering
  useEffect(() => {
    fetch('/api/emojis/list')
      .then(r => r.json())
      .then(list => {
        const map = {};
        (Array.isArray(list) ? list : []).forEach(e => { map[`custom:${e.name}`] = e.url; });
        setCustomEmojiMap(map);
      })
      .catch(() => {});
  }, []);
  // Подписка на обновления настроек размеров эмодзи
  useEffect(() => {
    const unsubscribe = subscribeEmojiSettings(() => {
      setEmojiSizes({ news: getCustomEmojiSizeForArea('news') });
    });
    return unsubscribe;
  }, []);
  // Keep editor DOM in sync only when content is changed programmatically
  useEffect(() => {
    if (editorRef.current && typeof form.content === 'string') {
      // Avoid unnecessary writes: only set when different
      if (editorRef.current.innerHTML !== form.content) {
        editorRef.current.innerHTML = form.content;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.content]);


  const exec = (cmd, value = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
      // Используем execCommand для простого форматирования
      document.execCommand(cmd, false, value);
      // Обновляем state из текущего HTML
      const currentHtml = editorRef.current ? editorRef.current.innerHTML : '';
      setForm(f => ({ ...f, content: currentHtml }));
    }
  };

  const insertCustomEmojiAtCursor = (token) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    const saved = editorSavedRangeRef.current;
    if (saved && editorRef.current.contains(saved.commonAncestorContainer)) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(saved);
    }
    const url = customEmojiMap[token];
    if (!url) {
      document.execCommand('insertText', false, token);
      const currentHtml = editorRef.current ? editorRef.current.innerHTML : '';
      setForm(f => ({ ...f, content: currentHtml }));
      return;
    }
    const img = document.createElement('img');
    img.src = url;
    img.alt = token;
    img.setAttribute('data-custom-emoji', 'true');
    img.setAttribute('data-token', token);
    const px = Number(emojiSizes.news || 24);
    img.style.width = px + 'px';
    img.style.height = px + 'px';
    img.style.objectFit = 'cover';
    img.style.verticalAlign = 'middle';
    img.style.margin = '0 2px';
    img.style.borderRadius = '6px';
    const range =
      window.getSelection && window.getSelection().rangeCount > 0
        ? window.getSelection().getRangeAt(0)
        : null;
    if (range && editorRef.current.contains(range.commonAncestorContainer)) {
      range.deleteContents();
      range.insertNode(img);
      range.setStartAfter(img);
      range.setEndAfter(img);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      editorRef.current.appendChild(img);
    }
    const currentHtml = editorRef.current ? editorRef.current.innerHTML : '';
    setForm(f => ({ ...f, content: currentHtml }));
  };

  const newsLocalFontOptionValue =
    newsCustomFontName && newsCustomFontDataUrl ? `'${newsCustomFontName}', Arial, sans-serif` : '';

  const titlePreviewColor = titleColor.trim() || '#0f172a';
  const contentPreviewColor = contentColor.trim() || '#1e293b';
  const titlePreviewSize = titleFontSize.trim() || '1.06em';
  const contentPreviewSize = contentFontSize.trim() || '1.06rem';
  const titlePreviewWeight = titleFontWeight.trim() || '700';
  const contentPreviewWeight = contentFontWeight.trim() || '400';

  const newsDesktopStyleRow = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'center',
    marginBottom: 8,
  };
  const newsDesktopStyleLabel = { fontSize: '0.82em', color: '#64748b', minWidth: 72 };

  const standardFontOptions = (
    <>
      <option value="Arial, sans-serif">Arial</option>
      <option value="'Times New Roman', serif">Times New Roman</option>
      <option value="'Courier New', monospace">Courier New</option>
      <option value="Georgia, serif">Georgia</option>
      <option value="Verdana, sans-serif">Verdana</option>
      <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
      <option value="Impact, sans-serif">Impact</option>
      <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
    </>
  );

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'relative' }}>
      <input
        name="title"
        placeholder="Заголовок новости (необязательно, если есть видео карточки)"
        value={form.title}
        onChange={handleChange}
        style={{
          padding: '10px 14px',
          borderRadius: 10,
          border: '1.5px solid #2193b0',
          fontWeight: titlePreviewWeight,
          fontSize: titlePreviewSize,
          color: titlePreviewColor,
          background: 'rgba(255,255,255,0.92)',
          fontFamily: titleFontFamily,
        }}
      />
      <div
        style={{
          padding: 12,
          borderRadius: 12,
          border: '1px solid #cbd5e1',
          background: '#f1f5f9',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92em' }}>Фон карточки в ленте</div>
        <div style={{ fontSize: '0.76em', color: '#64748b', lineHeight: 1.45 }}>
          Вся карточка новости. Фон области текста настраивается ниже в блоке «Оформление текста».
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            title="Образец фона карточки"
            style={{
              width: 36,
              height: 26,
              borderRadius: 6,
              border: '1px solid #94a3b8',
              flexShrink: 0,
              background: cardBackgroundId
                ? getNewsCardBackgroundCss(cardBackgroundId)
                : 'repeating-conic-gradient(#e2e8f0 0% 25%, #cbd5e1 0% 50%) 50% / 10px 10px',
            }}
          />
          <select
            value={cardBackgroundId}
            onChange={(e) => setCardBackgroundId(e.target.value)}
            aria-label="Фон карточки новости"
            style={{
              maxWidth: 280,
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: '0.92em',
            }}
          >
            <option value="">По умолчанию</option>
            <optgroup label="Светлые карточки">
              {NEWS_CARD_BACKGROUND_PRESETS.filter((p) => p.group === 'light').map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </optgroup>
            <optgroup label="Тёмные карточки">
              {NEWS_CARD_BACKGROUND_PRESETS.filter((p) => p.group === 'dark').map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </optgroup>
          </select>
        </div>
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: '1px dashed #cbd5e1',
          }}
        >
          <div style={{ fontWeight: 700, color: '#334155', fontSize: '0.88em', marginBottom: 6 }}>
            Фото фона карточки (два варианта)
          </div>
          <div style={{ fontSize: '0.76em', color: '#64748b', marginBottom: 10, lineHeight: 1.45 }}>
            Отдельно для телефона и для ПК. Если указан только один — он подставится на оба типа экрана. JPEG, PNG, GIF, WebP, до 8 МБ. Для одного варианта нельзя одновременно фото и видео — при загрузке одного второе сбрасывается.
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.82em', marginBottom: 4 }}>
              Для телефона
            </div>
            <input
              ref={newsCardImageInputRefMobile}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              disabled={newsCardImageUploadingMobile}
              onChange={(ev) => handleNewsCardImageFile(ev, 'mobile')}
              style={{ width: '100%', fontSize: '0.85em' }}
            />
            {newsCardImageUploadingMobile ? (
              <div style={{ marginTop: 4, fontSize: '0.78em', color: '#64748b' }}>Загрузка…</div>
            ) : null}
            {newsCardImageUrlMobile ? (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <img
                  src={newsCardImageUrlMobile}
                  alt=""
                  style={{
                    width: '100%',
                    maxHeight: 100,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setNewsCardImageUrlMobile('');
                    if (newsCardImageInputRefMobile.current) newsCardImageInputRefMobile.current.value = '';
                  }}
                  style={{
                    alignSelf: 'flex-start',
                    border: 'none',
                    background: 'transparent',
                    color: '#dc2626',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '0.82em',
                  }}
                >
                  Убрать (телефон)
                </button>
              </div>
            ) : null}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.82em', marginBottom: 4 }}>
              Для ПК / широкого экрана
            </div>
            <input
              ref={newsCardImageInputRefDesktop}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              disabled={newsCardImageUploadingDesktop}
              onChange={(ev) => handleNewsCardImageFile(ev, 'desktop')}
              style={{ width: '100%', fontSize: '0.85em' }}
            />
            {newsCardImageUploadingDesktop ? (
              <div style={{ marginTop: 4, fontSize: '0.78em', color: '#64748b' }}>Загрузка…</div>
            ) : null}
            {newsCardImageUrlDesktop ? (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <img
                  src={newsCardImageUrlDesktop}
                  alt=""
                  style={{
                    width: '100%',
                    maxHeight: 100,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setNewsCardImageUrlDesktop('');
                    if (newsCardImageInputRefDesktop.current) newsCardImageInputRefDesktop.current.value = '';
                  }}
                  style={{
                    alignSelf: 'flex-start',
                    border: 'none',
                    background: 'transparent',
                    color: '#dc2626',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '0.82em',
                  }}
                >
                  Убрать (ПК)
                </button>
              </div>
            ) : null}
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed #cbd5e1' }}>
            <div style={{ fontWeight: 700, color: '#334155', fontSize: '0.88em', marginBottom: 6 }}>
              Видео фона карточки (MP4, два варианта)
            </div>
            <div style={{ fontSize: '0.76em', color: '#64748b', marginBottom: 10, lineHeight: 1.45 }}>
              Как и фото: отдельно для телефона и ПК, до 100 МБ. Если загружено только видео, заголовок и текст не обязательны. На карточке без текста затемнение поверх ролика не накладывается.
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.82em', marginBottom: 4 }}>
                Видео для телефона
              </div>
              <input
                ref={newsCardVideoInputRefMobile}
                type="file"
                accept="video/mp4,.mp4"
                disabled={newsCardVideoUploadingMobile}
                onChange={(ev) => handleNewsCardVideoFile(ev, 'mobile')}
                style={{ width: '100%', fontSize: '0.85em' }}
              />
              {newsCardVideoUploadingMobile ? (
                <div style={{ marginTop: 4, fontSize: '0.78em', color: '#64748b' }}>Загрузка…</div>
              ) : null}
              {newsCardVideoUrlMobile ? (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <video
                    src={newsCardVideoUrlMobile}
                    muted
                    playsInline
                    controls
                    style={{
                      width: '100%',
                      maxHeight: 120,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setNewsCardVideoUrlMobile('');
                      if (newsCardVideoInputRefMobile.current) newsCardVideoInputRefMobile.current.value = '';
                    }}
                    style={{
                      alignSelf: 'flex-start',
                      border: 'none',
                      background: 'transparent',
                      color: '#dc2626',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: '0.82em',
                    }}
                  >
                    Убрать видео (телефон)
                  </button>
                </div>
              ) : null}
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.82em', marginBottom: 4 }}>
                Видео для ПК / широкого экрана
              </div>
              <input
                ref={newsCardVideoInputRefDesktop}
                type="file"
                accept="video/mp4,.mp4"
                disabled={newsCardVideoUploadingDesktop}
                onChange={(ev) => handleNewsCardVideoFile(ev, 'desktop')}
                style={{ width: '100%', fontSize: '0.85em' }}
              />
              {newsCardVideoUploadingDesktop ? (
                <div style={{ marginTop: 4, fontSize: '0.78em', color: '#64748b' }}>Загрузка…</div>
              ) : null}
              {newsCardVideoUrlDesktop ? (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <video
                    src={newsCardVideoUrlDesktop}
                    muted
                    playsInline
                    controls
                    style={{
                      width: '100%',
                      maxHeight: 120,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setNewsCardVideoUrlDesktop('');
                      if (newsCardVideoInputRefDesktop.current) newsCardVideoInputRefDesktop.current.value = '';
                    }}
                    style={{
                      alignSelf: 'flex-start',
                      border: 'none',
                      background: 'transparent',
                      color: '#dc2626',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: '0.82em',
                    }}
                  >
                    Убрать видео (ПК)
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          padding: 12,
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          background: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.95em' }}>Шрифты (Google Fonts)</div>
        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#334155', fontSize: '0.88em' }}>
            Шрифт заголовка
          </label>
          <select
            value={titleFontFamily}
            onChange={(e) => setTitleFontFamily(e.target.value)}
            style={newsDesktopFontSelectStyle}
          >
            {standardFontOptions}
            {newsLocalFontOptionValue ? (
              <option value={newsLocalFontOptionValue}>{newsCustomFontName} (локальный)</option>
            ) : null}
            {newsGoogleFontFamilies.filter(Boolean).map((name) => (
              <option key={`nt-${name}`} value={googleFontFamilyCssValue(name)}>
                {name} (Google Fonts)
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              googleFontsPickerApplyToRef.current = 'title';
              setGoogleFontsPickerOpen(true);
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid rgba(33, 147, 176, 0.45)',
              background: 'rgba(33, 147, 176, 0.1)',
              color: '#0e7490',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <FiExternalLink size={16} />
            Добавить из Google Fonts (заголовок)
          </button>
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e2e8f0' }}>
            <span style={{ display: 'block', fontWeight: 600, color: '#334155', fontSize: '0.85em', marginBottom: 8 }}>
              Цвет, размер и жирность заголовка
            </span>
            <div style={newsDesktopStyleRow}>
              <span style={newsDesktopStyleLabel}>Цвет</span>
              <input
                type="color"
                aria-label="Цвет заголовка"
                value={/^#[0-9a-fA-F]{6}$/i.test(titleColor.trim()) ? titleColor.trim() : '#0f172a'}
                onChange={(e) => setTitleColor(e.target.value)}
                style={{
                  width: 40,
                  height: 32,
                  padding: 0,
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              />
              <input
                type="text"
                value={titleColor}
                onChange={(e) => setTitleColor(e.target.value)}
                placeholder="#0f172a или rgb(…)"
                style={{
                  flex: 1,
                  minWidth: 120,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.9em',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={newsDesktopStyleRow}>
              <span style={newsDesktopStyleLabel}>Размер</span>
              <input
                type="text"
                value={titleFontSize}
                onChange={(e) => setTitleFontSize(e.target.value)}
                placeholder="напр. 1.06em, 18px"
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.9em',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ ...newsDesktopStyleRow, marginBottom: 0 }}>
              <span style={newsDesktopStyleLabel}>Жирность</span>
              <select
                value={titleFontWeight}
                onChange={(e) => setTitleFontWeight(e.target.value)}
                style={{ flex: 1, minWidth: 0, ...newsDesktopFontSelectStyle, marginBottom: 0 }}
              >
                <option value="">По умолчанию</option>
                <option value="300">300</option>
                <option value="400">400</option>
                <option value="500">500</option>
                <option value="600">600</option>
                <option value="700">700</option>
                <option value="800">800</option>
                <option value="900">900</option>
                <option value="normal">normal</option>
                <option value="bold">bold</option>
              </select>
            </div>
          </div>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#334155', fontSize: '0.88em' }}>
            Шрифт текста
          </label>
          <select
            value={contentFontFamily}
            onChange={(e) => setContentFontFamily(e.target.value)}
            style={newsDesktopFontSelectStyle}
          >
            {standardFontOptions}
            {newsLocalFontOptionValue ? (
              <option value={newsLocalFontOptionValue}>{newsCustomFontName} (локальный)</option>
            ) : null}
            {newsGoogleFontFamilies.filter(Boolean).map((name) => (
              <option key={`nc-${name}`} value={googleFontFamilyCssValue(name)}>
                {name} (Google Fonts)
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              googleFontsPickerApplyToRef.current = 'content';
              setGoogleFontsPickerOpen(true);
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid rgba(231, 76, 60, 0.4)',
              background: 'rgba(231, 76, 60, 0.08)',
              color: '#c2410c',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <FiExternalLink size={16} />
            Добавить из Google Fonts (текст)
          </button>
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e2e8f0' }}>
            <span style={{ display: 'block', fontWeight: 600, color: '#334155', fontSize: '0.85em', marginBottom: 8 }}>
              Оформление текста
            </span>
            <div style={newsDesktopStyleRow}>
              <span style={newsDesktopStyleLabel}>Цвет</span>
              <input
                type="color"
                aria-label="Цвет текста новости"
                value={/^#[0-9a-fA-F]{6}$/i.test(contentColor.trim()) ? contentColor.trim() : '#1e293b'}
                onChange={(e) => setContentColor(e.target.value)}
                style={{
                  width: 40,
                  height: 32,
                  padding: 0,
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              />
              <input
                type="text"
                value={contentColor}
                onChange={(e) => setContentColor(e.target.value)}
                placeholder="#1e293b или rgb(…)"
                style={{
                  flex: 1,
                  minWidth: 120,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.9em',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ ...newsDesktopStyleRow, alignItems: 'center' }}>
              <span style={newsDesktopStyleLabel}>Фон текста</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                <span
                  title="Образец фона"
                  style={{
                    width: 36,
                    height: 28,
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    flexShrink: 0,
                    background: contentBackgroundId
                      ? getNewsContentBackgroundCss(contentBackgroundId)
                      : 'repeating-conic-gradient(#f1f5f9 0% 25%, #e2e8f0 0% 50%) 50% / 10px 10px',
                  }}
                />
                <select
                  value={contentBackgroundId}
                  onChange={(e) => setContentBackgroundId(e.target.value)}
                  aria-label="Фон области текста новости"
                  style={{
                    flex: '1 1 160px',
                    minWidth: 120,
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9em',
                  }}
                >
                  <option value="">Без фона</option>
                  <optgroup label="Светлые">
                    {NEWS_CONTENT_BACKGROUND_PRESETS.filter((p) => p.group === 'light').map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Тёмные">
                    {NEWS_CONTENT_BACKGROUND_PRESETS.filter((p) => p.group === 'dark').map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
            </div>
            <div style={newsDesktopStyleRow}>
              <span style={newsDesktopStyleLabel}>Размер</span>
              <input
                type="text"
                value={contentFontSize}
                onChange={(e) => setContentFontSize(e.target.value)}
                placeholder="напр. 1.06rem, 16px"
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.9em',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ ...newsDesktopStyleRow, marginBottom: 0 }}>
              <span style={newsDesktopStyleLabel}>Жирность</span>
              <select
                value={contentFontWeight}
                onChange={(e) => setContentFontWeight(e.target.value)}
                style={{ flex: 1, minWidth: 0, ...newsDesktopFontSelectStyle, marginBottom: 0 }}
              >
                <option value="">По умолчанию</option>
                <option value="300">300</option>
                <option value="400">400</option>
                <option value="500">500</option>
                <option value="600">600</option>
                <option value="700">700</option>
                <option value="800">800</option>
                <option value="900">900</option>
                <option value="normal">normal</option>
                <option value="bold">bold</option>
              </select>
            </div>
          </div>
        </div>
        {newsGoogleFontFamilies.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {newsGoogleFontFamilies.map((name) => (
              <span
                key={name}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: '#e2e8f0',
                  color: '#334155',
                  fontSize: '0.82em',
                }}
              >
                {name}
                <button
                  type="button"
                  onClick={() => removeNewsGoogleFont(name)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#dc2626',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '1.1em',
                    lineHeight: 1,
                  }}
                  aria-label={`Убрать ${name}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div style={{ fontSize: '0.78em', color: '#64748b', lineHeight: 1.45 }}>
          Список шрифтов хранится на сервере: его видят все, кто может создавать новости (мобильная и десктопная формы).
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#334155', fontSize: '0.88em' }}>
            URL CSS шрифта (опционально)
          </label>
          <div style={{ fontSize: '0.78em', color: '#64748b', marginBottom: 6, lineHeight: 1.45 }}>
            Например ссылка на CSS2 с fonts.googleapis.com (как в мобильной форме).
          </div>
          <input
            type="text"
            value={newsCustomFontUrl}
            onChange={(e) => setNewsCustomFontUrl(e.target.value)}
            placeholder="URL шрифта (CSS)"
            style={{ ...newsDesktopFontSelectStyle, marginBottom: 0 }}
          />
        </div>
        <div>
          <div style={{ fontSize: '0.85em', color: '#64748b', marginBottom: 6 }}>
            Локальный файл шрифта (.ttf, .otf, .woff, .woff2):
          </div>
          <input
            type="file"
            accept=".ttf,.otf,.woff,.woff2"
            onChange={handleNewsFontFileUpload}
            style={{ width: '100%', fontSize: '0.9em', color: '#334155' }}
          />
          {newsCustomFontDataUrl && newsCustomFontName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ color: '#16a34a', fontSize: '0.88em' }}>✓ Загружен: {newsCustomFontName}</span>
              <button
                type="button"
                onClick={() => {
                  const localVal = newsLocalFontOptionValue;
                  setNewsCustomFontDataUrl('');
                  setNewsCustomFontName('');
                  setTitleFontFamily((f) => (f === localVal ? 'Arial, sans-serif' : f));
                  setContentFontFamily((f) => (f === localVal ? 'Arial, sans-serif' : f));
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#dc2626',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.88em',
                }}
              >
                Сбросить локальный шрифт
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <div style={{ position: 'relative' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" onClick={() => exec('bold')} style={{ background: '#e0f7fa', border: '1px solid #b2ebf2', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 800 }}>B</button>
          <button type="button" onClick={() => exec('italic')} style={{ background: '#e8eaf6', border: '1px solid #c5cae9', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontStyle: 'italic' }}>I</button>
          <button type="button" onClick={() => exec('underline')} style={{ background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', textDecoration: 'underline' }}>U</button>
          <button type="button" onClick={() => exec('insertUnorderedList')} style={{ background: '#f1f8e9', border: '1px solid #dcedc8', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>• Список</button>
          <button type="button" onClick={() => exec('insertOrderedList')} style={{ background: '#f1f8e9', border: '1px solid #dcedc8', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>1. Список</button>
          <button type="button" onClick={() => exec('justifyLeft')} style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>⟸</button>
          <button type="button" onClick={() => exec('justifyCenter')} style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>⟷</button>
          <button type="button" onClick={() => exec('justifyRight')} style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>⟹</button>
          <button type="button" onClick={() => exec('formatBlock', 'H1')} style={{ background: '#ede7f6', border: '1px solid #d1c4e9', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 800 }}>H1</button>
          <button type="button" onClick={() => exec('formatBlock', 'H2')} style={{ background: '#ede7f6', border: '1px solid #d1c4e9', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 800 }}>H2</button>
          <button type="button" onClick={() => exec('formatBlock', 'H3')} style={{ background: '#ede7f6', border: '1px solid #d1c4e9', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 800 }}>H3</button>
          <button type="button" onClick={() => exec('formatBlock', 'P')} style={{ background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 700 }}>Обычный</button>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff' }}>
            Цвет
            <input
              type="color"
              value={textColor}
              onChange={(e) => {
                const v = e.target.value;
                setTextColor(v);
                setContentColor(v);
                exec('foreColor', v);
              }}
              style={{ width: 26, height: 26, border: 'none', background: 'transparent', padding: 0 }}
            />
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff' }}>
            Маркер
            <input type="color" value={bgColor} onChange={(e) => { setBgColor(e.target.value); exec('hiliteColor', e.target.value); }} style={{ width: 26, height: 26, border: 'none', background: 'transparent', padding: 0 }} />
          </label>
          <button type="button" onClick={() => exec('removeFormat')} style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>Очистить</button>
        </div>
        {/* Rich editor */}
        <div
          ref={editorRef}
          contentEditable
          dir="ltr"
          onInput={(e) => {
            const html = e.currentTarget ? e.currentTarget.innerHTML : '';
            setForm(f => ({ ...f, content: html }));
          }}
          suppressContentEditableWarning
          style={{
            width: '100%',
            minHeight: 140,
            padding: '14px 52px 14px 18px',
            borderRadius: 12,
            border: '1.5px solid #e74c3c',
            background: getNewsContentBackgroundCss(contentBackgroundId) || '#fff',
            fontSize: contentPreviewSize,
            fontWeight: contentPreviewWeight,
            color: contentPreviewColor,
            fontFamily: contentFontFamily,
            outline: 'none',
            overflowY: 'auto',
            textAlign: 'left',
            direction: 'ltr',
            unicodeBidi: 'bidi-override',
          }}
        />
        <button
          type="button"
          onPointerDown={captureEditorRange}
          onClick={() => setShowEmoji(v => !v)}
          title="Эмодзи"
          style={{ position: 'absolute', right: 8, top: 48, background: 'linear-gradient(135deg, #ffe082 0%, #fcb69f 100%)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', boxShadow: '0 1px 6px #e74c3c33', color: '#232931', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <FiSmile size={20} />
        </button>
        {showEmoji && (
          <div style={{ position: 'absolute', left: 0, top: '100%', marginTop: 100, zIndex: 1000, width: '1120px', maxWidth: 'calc(100% - 16px)' }} onClick={e => e.stopPropagation()}>
            <CustomEmojiPicker
              isOpen={showEmoji}
              onClose={() => setShowEmoji(false)}
              onEmojiSelect={(emoji) => {
                const str = String(emoji || '');
                if (str.startsWith('custom:')) insertCustomEmojiAtCursor(str);
                else if (editorRef.current) {
                  editorRef.current.focus();
                  const saved = editorSavedRangeRef.current;
                  if (saved && editorRef.current.contains(saved.commonAncestorContainer)) {
                    const sel = window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(saved);
                  }
                  document.execCommand('insertText', false, str);
                  const currentHtml = editorRef.current ? editorRef.current.innerHTML : '';
                  setForm(f => ({ ...f, content: currentHtml }));
                } else {
                  setForm(f => ({ ...f, content: (f.content || '') + str }));
                }
              }}
            />
          </div>
        )}
      </div>
      <button type="submit" style={{ background: 'linear-gradient(135deg, #2193b0 0%, #43e97b 100%)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px #43e97b22, 0 0 0 2px #2193b044', opacity: isSending ? 0.7 : 1 }} disabled={isSending}>
        {isSending ? 'Добавление...' : 'Добавить'}
      </button>

      {googleFontsPickerOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Google Fonts"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10050,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setGoogleFontsPickerOpen(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 440,
              maxHeight: '85vh',
              background: '#111827',
              borderRadius: 14,
              border: '1px solid #374151',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '12px 14px',
                borderBottom: '1px solid #374151',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <span style={{ fontWeight: 800, color: '#f9fafb' }}>Google Fonts</span>
              <button
                type="button"
                onClick={() => setGoogleFontsPickerOpen(false)}
                style={{
                  border: 'none',
                  background: '#1f2937',
                  color: '#e5e7eb',
                  borderRadius: 8,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Закрыть
              </button>
            </div>
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                onClick={() => window.open('https://fonts.google.com', '_blank', 'noopener,noreferrer')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: 10,
                  borderRadius: 10,
                  border: '1px solid #4b5563',
                  background: '#1e3a5f',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                <FiExternalLink size={16} />
                fonts.google.com
              </button>
              <input
                type="search"
                value={googleFontsSearch}
                onChange={(e) => setGoogleFontsSearch(e.target.value)}
                placeholder="Поиск в каталоге…"
                style={{
                  width: '100%',
                  padding: 10,
                  borderRadius: 8,
                  border: '1px solid #374151',
                  background: '#0e1420',
                  color: '#fff',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={manualGoogleFontName}
                  onChange={(e) => setManualGoogleFontName(e.target.value)}
                  placeholder="Имя с сайта, например Caveat Brush"
                  style={{
                    flex: 1,
                    padding: 10,
                    borderRadius: 8,
                    border: '1px solid #374151',
                    background: '#0e1420',
                    color: '#fff',
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  type="button"
                  onClick={() => addNewsGoogleFont(manualGoogleFontName)}
                  style={{
                    padding: '0 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#a3e635',
                    color: '#0f172a',
                    fontWeight: 800,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Добавить
                </button>
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '4px 10px 12px' }}>
              {GOOGLE_FONTS_WELCOME_CATALOG.filter((f) =>
                f.toLowerCase().includes(googleFontsSearch.trim().toLowerCase())
              )
                .slice(0, 120)
                .map((family) => (
                  <button
                    key={family}
                    type="button"
                    onClick={() => addNewsGoogleFont(family)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      marginBottom: 4,
                      borderRadius: 8,
                      border: '1px solid #2f3440',
                      background: '#0e1420',
                      color: '#e5e7eb',
                      cursor: 'pointer',
                    }}
                  >
                    {family}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

export default NewsModal;
